/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import type { BookingResponse } from '@/lib/schemas/booking';

export interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  accent?: string;
  borderAccent?: string;
  textAccent: string;
  description?: string;
}

export const getCardTheme = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes('booked') || normalized.includes('students')) {
    return {
      iconBg: 'bg-blue-50 text-blue-600 dark:bg-slate-900 dark:text-blue-400 border border-slate-100 dark:border-slate-800',
      desc: normalized.includes('students') ? 'Unique learners engaged' : 'Total classes reserved'
    };
  }
  if (normalized.includes('upcoming') || normalized.includes('hours')) {
    return {
      iconBg: 'bg-violet-50 text-violet-600 dark:bg-slate-900 dark:text-violet-400 border border-slate-100 dark:border-slate-800',
      desc: normalized.includes('upcoming') ? 'Scheduled future classes' : 'Total teaching hours'
    };
  }
  if (normalized.includes('completed') || normalized.includes('taught') || normalized.includes('classes taught')) {
    return {
      iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-slate-900 dark:text-emerald-400 border border-slate-100 dark:border-slate-800',
      desc: 'Successfully finished'
    };
  }
  if (normalized.includes('awaiting') || normalized.includes('earnings') || normalized.includes('net earnings')) {
    return {
      iconBg: 'bg-amber-50 text-amber-600 dark:bg-slate-900 dark:text-amber-400 border border-slate-100 dark:border-slate-800',
      desc: normalized.includes('awaiting') ? 'Pending tutor response' : 'Net payout balance'
    };
  }
  return {
    iconBg: 'bg-slate-50 text-slate-600 dark:bg-slate-900 dark:text-slate-400 border border-slate-100 dark:border-slate-800',
    desc: ''
  };
};

export function StatCard({ icon, label, value, textAccent, description }: StatCardProps) {
  const theme = getCardTheme(label);
  const descText = description || theme.desc;

  return (
    <div className="h-full relative overflow-hidden rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {label}
          </p>
          <div className="space-y-1">
            <p className={`text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 leading-none ${textAccent}`}>
              {value}
            </p>
            {descText && (
              <p className="text-xs text-slate-400 dark:text-slate-500 leading-snug">
                {descText}
              </p>
            )}
          </div>
        </div>
        
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${theme.iconBg}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

export function isUpcoming(booking: BookingResponse, now: number): boolean {
  return (
    (booking.status === 'pending' || booking.status === 'accepted') &&
    new Date(booking.slotEndUtc).getTime() > now
  );
}

export function formatClassDate(date: Date): string {
  const today = new Date();
  const tomorrow = new Date(Date.now() + 86400000);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
